import { createRouter, createWebHistory } from 'vue-router';
import store from "@/store";
import i18n from '@/plugins/i18n';
import { piniaOidcCreateRouterMiddleware } from 'pinia-oidc';
import { useOidc } from '@/composables/oidc';

import PublicPage from '@/views/public/PublicPage.vue';
import ApplicationDetailsPage from '@/views/applications/ApplicationDetailsPage.vue';
import JournalDetailsPage from '@/views/applications/journal/JournalDetailsPage.vue';
import OrgApplicationsPage from '@/views/orgapplications/OrgApplicationsPage.vue';
import SettingsPage from '@/views/generalsettings/SettingsPage.vue';
import RateConfigurationPage from '@/views/rateconfigurations/RateConfigurationPage.vue';
import OidcCallback from '@/views/oidc/OidcCallbackPage.vue';
import OidcCallbackError from '@/views/oidc/OidcCallbackErrorPage.vue';
import ErrorPage from "@/views/error/ErrorPage.vue";
import StatisticsPage from '@/views/statistics/StatisticsPage.vue';
import OrgFeesInvoicePage from '@/views/orgFeesInvoice/OrgFeesInvoicePage.vue';
import StartPage from '@/views/start/StartPage.vue';
import SenderInformationPage from '@/views/senderInformation/SenderInformationPage.vue';
import InfoMessagesPage from '@/views/messages/MessageAdminPage.vue';

const applicationGuard = async function (to, _from, next) {
  try {
    await store.dispatch("application/currentApplicationStore/fetch", to.params.id);
    next();
  } catch (error) {
    await store.commit("errorStore/set", error || {});
    next('error');
  }
};

const router = createRouter({
  history: createWebHistory(),
  base: process.env.BASE_URL,
  routes: [
    {
      path: '/',
      name: 'public',
      component: PublicPage,
      meta: {
        isPublic: true,
        layout: 'public'
      }
    },
    {
      path: '/messages',
      name: 'messages',
      component: InfoMessagesPage,
      meta: {
        title: i18n.global.t("infoMessagesStrings.pageTitle")
      }
    },
    {
      path: '/backoffice',
      name: 'backoffice',
      component: SettingsPage,
      meta: {
        title: i18n.global.t("backofficeStrings.pageTitle")
      }
    },
    {
      path: '/backoffice/period/create',
      name: 'createRateConfiguration',
      component: RateConfigurationPage,
      meta: {
        title: i18n.global.t("rateConfigurationStrings.pageTitle")
      }
    },
    {
      path: '/backoffice/period/:id',
      name: 'rateConfigurationDetails',
      component: RateConfigurationPage,
      meta: {
        title: i18n.global.t("rateConfigurationStrings.pageTitle")
      }
    },
    {
      path: '/orgapplications/:id/orgs/:orgid/journal',
      name: 'journalDetails',
      component: JournalDetailsPage,
      beforeEnter: applicationGuard,
      meta: {
        title: i18n.global.t("journalStrings.pageTitle"),
        addTitleId: true,
        addTitleOrgName: true
      }
    },
    {
      path: '/orgapplications',
      name: 'orgApplications',
      component: OrgApplicationsPage,
      meta: {
        title: i18n.global.t("orgApplicationsStrings.pageTitle")
      }
    },
    {
      path: '/senderInformation',
      name: 'senderInformation',
      component: SenderInformationPage,
      meta: {
        title: i18n.global.t("senderInformationStrings.pageTitle")
      }
    },
    {
      path: '/orgapplications/:id',
      name: 'orgApplicationDetails',
      component: ApplicationDetailsPage,
      beforeEnter: applicationGuard,
      meta: {
        title: i18n.global.t("editApplicationStrings.detailsPageTitle"),
        addTitleId: true
      }
    },
    {
      path: '/statistics',
      name: 'statistics',
      component: StatisticsPage,
      meta: {
        title: i18n.global.t("statisticsStrings.pageTitle")
      }
    },
    {
      path: '/orgFeesInvoice',
      name: 'orgFeesInvoice',
      component: OrgFeesInvoicePage,
      meta: {
        title: i18n.global.t("orgFeesInvoiceStrings.pageTitle")
      }
    },
    {
      path: '/start',
      name: 'start',
      component: StartPage,
      meta: {
        title: i18n.global.t("startPageStrings.pageTitle"),
        styleOverride: 'image-title'
      }
    },
    // Needs to match redirectUri in you oidcSettings
    {
      path: '/oidc-callback',
      name: 'oidcCallback',
      component: OidcCallback
    },
    // Needs to match redirect_uri in you oidcSettings
    {
      path: '/oidc-callback-error',
      name: 'oidcCallbackError',
      component: OidcCallbackError,
      meta: {
        isPublic: true
      }
    },
    {
      path: '/error',
      name: 'error',
      component: ErrorPage,
      beforeEnter: function (_to, _from, next) {
        if (store.getters['errorStore/error']) {
          next();
        } else {
          next('orgapplications');
        }
      }
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'default',
      component: ErrorPage,
      beforeEnter: async function (_to, _from, next) {
        const notFound = i18n.global.t(`httpErrorCodes.404`);
        await store.commit("errorStore/set", notFound);
        next();
      }
    }
  ]
});

const oidcMiddleware = (() => {
  let oidcMiddleware;
  return (to, from, next) => {
    if (!oidcMiddleware) {
      oidcMiddleware = piniaOidcCreateRouterMiddleware(useOidc());
    }
    return oidcMiddleware(to, from, next);
  };
})();

router.beforeEach(oidcMiddleware);

export default router;
